import { ethers } from "ethers";
import { Options } from "@layerzerolabs/lz-v2-utilities";
import {
  NIBIRU_EVM_CHAIN_ID,
  NIBIRU_WALLET_RPC,
  NIBIRU_EID,
  NATIVE_DROP_NIBI_AMOUNT,
  OFT_SLIPPAGE_BPS,
  OFT_ABI,
  calcMinAmountLD,
  buildSendParam,
  execFee,
} from "./bridgeConstants";

// ─── Options builders ───────────────────────────────────────────

/** Deposit options: lzReceive gas + 1 NIBI native drop to recipient */
export function buildDepositOptions(recipientBytes32) {
  return Options.newOptions()
    .addExecutorLzReceiveOption(20000, 0)
    .addExecutorNativeDropOption(NATIVE_DROP_NIBI_AMOUNT, recipientBytes32)
    .toHex()
    .toString();
}

/** Withdraw options: empty (no native drop needed) */
export function buildWithdrawOptions() {
  return Options.newOptions().toHex().toString();
}

// ─── quoteOFT wrapper ──────────────────────────────────────────

/**
 * Call quoteOFT on the adapter contract to get minAmountLD and protocol fee.
 * Falls back to a simple slippage calc if the call fails.
 */
export async function quoteMinAmountLD(oft, dstEid, recipientBytes32, tokensToSend, options) {
  const probe = buildSendParam(dstEid, recipientBytes32, tokensToSend, ethers.toBigInt(0), options);

  try {
    const result = await oft.quoteOFT(probe);
    const amountReceivedLD = ethers.toBigInt(result[2].amountReceivedLD);
    console.log(`[Bridge] quoteOFT: sent=${ethers.formatUnits(tokensToSend, 6)}, received=${ethers.formatUnits(amountReceivedLD, 6)}`);
    const minAmountLD =
      (amountReceivedLD * (ethers.toBigInt(10000) - OFT_SLIPPAGE_BPS)) / ethers.toBigInt(10000);
    const protocolFeeLD =
      tokensToSend > amountReceivedLD
        ? tokensToSend - amountReceivedLD
        : ethers.toBigInt(0);
    return { amountReceivedLD, minAmountLD, protocolFeeLD };
  } catch (err) {
    console.warn(`[Bridge] quoteOFT failed, using fallback:`, err.message);
    return {
      amountReceivedLD: tokensToSend,
      minAmountLD: calcMinAmountLD(tokensToSend),
      protocolFeeLD: ethers.toBigInt(0),
    };
  }
}

// ─── Chain switching ────────────────────────────────────────────

/** Switch wallet to target chain. Add Nibiru if unrecognized. */
export async function ensureChain(provider, chainId) {
  const hexChainId = "0x" + chainId.toString(16);
  const network = await provider.getNetwork().catch(() => null);
  if (network && Number(network.chainId) === chainId) return;

  try {
    await provider.send("wallet_switchEthereumChain", [{ chainId: hexChainId }]);
  } catch (err) {
    const code = err?.code;
    const message = err?.message || String(err);
    const lower = message.toLowerCase();
    const unrecognized =
      code === 4902 ||
      (code === -32603 && lower.includes("unrecognized chain")) ||
      message.includes("4902") ||
      lower.includes("unrecognized chain");

    if (!unrecognized || chainId !== NIBIRU_EVM_CHAIN_ID) throw err;

    await provider.send("wallet_addEthereumChain", [
      {
        chainId: hexChainId,
        chainName: "Nibiru EVM",
        nativeCurrency: { name: "Nibiru", symbol: "NIBI", decimals: 18 },
        rpcUrls: [NIBIRU_WALLET_RPC],
        blockExplorerUrls: ["https://nibiscan.io"],
      },
    ]);
  }
}

/** Poll eth_chainId until wallet reports the target chain */
export async function waitForChain(provider, chainId) {
  const hexChainId = "0x" + chainId.toString(16);
  for (let i = 0; i < 25; i++) {
    const result = await provider.send("eth_chainId", []).catch(() => null);
    const current = typeof result === "string" ? result : result?.result;
    if (current?.toLowerCase() === hexChainId.toLowerCase()) return;
    await new Promise((resolve) => setTimeout(resolve, 500));
  }
  throw new Error("Wallet did not switch to chain " + chainId);
}

// ─── Bridge signer ──────────────────────────────────────────────

/**
 * Creates a signer that sends through the wallet provider but reads
 * receipts from a separate RPC provider. This avoids ethers NETWORK_ERROR
 * after wallet chain switching.
 */
export function createBridgeSigner(walletProvider, readProvider, chainId, fromAddress) {
  return {
    provider: readProvider,
    getAddress: () => Promise.resolve(fromAddress),
    sendTransaction: async (tx) => {
      const nonce =
        tx.nonce ?? (await readProvider.getTransactionCount(fromAddress, "pending"));
      const feeData = await readProvider.getFeeData();
      const gasLimit = tx.gasLimit ?? ethers.toBigInt(300000);
      const maxFeePerGas = tx.maxFeePerGas ?? feeData.maxFeePerGas;
      const maxPriorityFeePerGas = tx.maxPriorityFeePerGas ?? feeData.maxPriorityFeePerGas;

      const hasFees =
        maxFeePerGas != null &&
        ethers.getBigInt(maxFeePerGas) > ethers.toBigInt(0) &&
        maxPriorityFeePerGas != null &&
        ethers.getBigInt(maxPriorityFeePerGas) > ethers.toBigInt(0);

      const feeOverrides = hasFees
        ? { type: 2, maxFeePerGas, maxPriorityFeePerGas }
        : {};

      const fullTx = {
        from: fromAddress,
        to: tx.to,
        data: tx.data,
        value: tx.value ?? 0,
        chainId,
        nonce,
        gasLimit,
        ...feeOverrides,
      };

      // Convert to RPC-compatible hex format
      const rpcTx = {
        from: fullTx.from,
        data: fullTx.data ?? "0x",
        value: fullTx.value != null ? ethers.toBeHex(fullTx.value) : "0x0",
      };
      if (fullTx.to != null) rpcTx.to = fullTx.to.toString();
      if (fullTx.gasLimit != null) rpcTx.gas = ethers.toBeHex(fullTx.gasLimit);
      if (fullTx.nonce != null) rpcTx.nonce = ethers.toBeHex(fullTx.nonce);
      if (fullTx.chainId != null) rpcTx.chainId = ethers.toBeHex(fullTx.chainId);
      if (fullTx.type === 2) {
        rpcTx.type = "0x2";
        if (fullTx.maxFeePerGas != null)
          rpcTx.maxFeePerGas = ethers.toBeHex(fullTx.maxFeePerGas);
        if (fullTx.maxPriorityFeePerGas != null)
          rpcTx.maxPriorityFeePerGas = ethers.toBeHex(fullTx.maxPriorityFeePerGas);
      }

      const txHash = await walletProvider.send("eth_sendTransaction", [rpcTx]);
      if (typeof txHash !== "string") {
        throw new Error("eth_sendTransaction did not return a tx hash");
      }

      return {
        hash: txHash,
        wait: () => waitForReceipt(readProvider, txHash),
        getReceipt: () => readProvider.getTransactionReceipt(txHash),
      };
    },
  };
}

/** Poll for tx receipt with retry */
async function waitForReceipt(readProvider, txHash, maxAttempts = 60, delayMs = 2000) {
  for (let i = 0; i < maxAttempts; i++) {
    const receipt = await readProvider.getTransactionReceipt(txHash);
    if (receipt && receipt.blockNumber != null) return receipt;
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }
  throw new Error("Transaction confirmation timed out");
}

// ─── Retry logic ────────────────────────────────────────────────

/** Check if error is a transient network error worth retrying */
export function isNetworkError(err) {
  const msg = err?.message || String(err);
  return (
    msg.includes("network changed") ||
    msg.includes("NETWORK_ERROR") ||
    err?.code === "NETWORK_ERROR"
  );
}

// ─── Quote helpers ──────────────────────────────────────────────

/** Fetch deposit quote (fee in source chain native token) */
export async function fetchDepositQuote(chain, amountDisplay, toAddress) {
  const amountNum = parseFloat(amountDisplay);
  if (!amountDisplay || isNaN(amountNum) || amountNum <= 0) return null;

  const readProvider = new ethers.JsonRpcProvider(chain.rpcUrl, {
    chainId: chain.chainId,
    name: chain.id,
    ensAddress: null,
  });
  const localDecimals = chain.usdcDecimals || 6;
  const tokensToSend = ethers.parseUnits(amountDisplay, localDecimals);
  const recipient =
    toAddress?.startsWith("0x") && toAddress.length === 42
      ? toAddress
      : ethers.ZeroAddress;
  const recipientBytes32 = ethers.zeroPadValue(recipient.toLowerCase(), 32);
  const options = buildDepositOptions(recipientBytes32);
  const sendParam = buildSendParam(
    NIBIRU_EID,
    recipientBytes32,
    tokensToSend,
    calcMinAmountLD(tokensToSend),
    options
  );

  const oft = new ethers.Contract(chain.oftAdapter, OFT_ABI, readProvider);
  console.log(`[Bridge] Fetching deposit quote: chain=${chain.name}, adapter=${chain.oftAdapter}, amount=${amountDisplay}`);
  const result = await oft.quoteSend(sendParam, false);
  const fee = execFee(ethers.toBigInt(result.nativeFee));

  return {
    nativeFee: fee,
    feeDisplay: parseFloat(ethers.formatEther(fee)),
    feeSymbol: chain.nativeSymbol,
  };
}

/** Fetch withdraw quote (fee in NIBI, plus min received) */
export async function fetchWithdrawQuote(destChain, amountDisplay, toAddress) {
  const amountNum = parseFloat(amountDisplay);
  if (!amountDisplay || isNaN(amountNum) || amountNum <= 0) return null;

  const { NIBIRU_USDC_ADAPTER } = require("./bridgeConstants");
  const readProvider = new ethers.JsonRpcProvider(
    process.env.REACT_APP_NIBIRU_EVM_RPC || "https://evm-rpc.nibiru.fi",
    { chainId: 6900, name: "nibiru", ensAddress: null }
  );
  const tokensToSend = ethers.parseUnits(amountDisplay, 6);
  const recipient =
    toAddress?.startsWith("0x") && toAddress.length === 42
      ? toAddress
      : ethers.ZeroAddress;
  const recipientBytes32 = ethers.zeroPadValue(recipient.toLowerCase(), 32);
  const options = buildWithdrawOptions();
  const oft = new ethers.Contract(NIBIRU_USDC_ADAPTER, OFT_ABI, readProvider);

  const { minAmountLD, protocolFeeLD } = await quoteMinAmountLD(
    oft,
    destChain.eid,
    recipientBytes32,
    tokensToSend,
    options
  );

  const sendParam = buildSendParam(
    destChain.eid,
    recipientBytes32,
    tokensToSend,
    ethers.toBigInt(0),
    options
  );
  const result = await oft.quoteSend(sendParam, false);
  const fee = execFee(ethers.toBigInt(result.nativeFee));

  return {
    nativeFee: fee,
    feeDisplay: parseFloat(ethers.formatEther(fee)),
    feeSymbol: "NIBI",
    protocolFeeDisplay: parseFloat(ethers.formatUnits(protocolFeeLD, 6)),
    minReceivedDisplay: parseFloat(ethers.formatUnits(minAmountLD, 6)),
  };
}
